import React, { useState } from 'react';
import { BarChart3Icon, UsersIcon, CreditCardIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, CalendarIcon, CoffeeIcon, DropletIcon, FilterIcon, DownloadIcon, RefreshCwIcon, BellIcon, MegaphoneIcon, UserIcon, PlusIcon, PackageIcon, CheckIcon, AlertTriangleIcon, ClockIcon, SendIcon, ImageIcon, TargetIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon, XIcon, MapPinIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Button from '../ui/Button';
import GlassCard from '../ui/GlassCard';
interface BaristaAdminProps {
  onLogout: () => void;
}
const BaristaAdmin: React.FC<BaristaAdminProps> = ({
  onLogout
}) => {
  const [timeRange, setTimeRange] = useState('week');
  const [location, setLocation] = useState('all');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  // Mock data for charts
  const consumptionData = [{
    name: 'Mon',
    regular: 65,
    sparkling: 40,
    flavor: 24
  }, {
    name: 'Tue',
    regular: 59,
    sparkling: 30,
    flavor: 28
  }, {
    name: 'Wed',
    regular: 80,
    sparkling: 50,
    flavor: 36
  }, {
    name: 'Thu',
    regular: 81,
    sparkling: 55,
    flavor: 40
  }, {
    name: 'Fri',
    regular: 90,
    sparkling: 70,
    flavor: 50
  }, {
    name: 'Sat',
    regular: 110,
    sparkling: 90,
    flavor: 70
  }, {
    name: 'Sun',
    regular: 95,
    sparkling: 60,
    flavor: 45
  }];
  const userActivityData = [{
    name: 'Mon',
    newUsers: 5,
    activeUsers: 28
  }, {
    name: 'Tue',
    newUsers: 3,
    activeUsers: 25
  }, {
    name: 'Wed',
    newUsers: 8,
    activeUsers: 32
  }, {
    name: 'Thu',
    newUsers: 6,
    activeUsers: 35
  }, {
    name: 'Fri',
    newUsers: 10,
    activeUsers: 42
  }, {
    name: 'Sat',
    newUsers: 15,
    activeUsers: 50
  }, {
    name: 'Sun',
    newUsers: 12,
    activeUsers: 45
  }];
  const membershipData = [{
    name: 'Free',
    value: 60
  }, {
    name: 'Basic',
    value: 25
  }, {
    name: 'Premium',
    value: 15
  }];
  const COLORS = ['#00E4BB', '#A6FF4D', '#FF9800'];
  const revenueData = [{
    name: 'Mon',
    amount: 250
  }, {
    name: 'Tue',
    amount: 230
  }, {
    name: 'Wed',
    amount: 310
  }, {
    name: 'Thu',
    amount: 350
  }, {
    name: 'Fri',
    amount: 420
  }, {
    name: 'Sat',
    amount: 550
  }, {
    name: 'Sun',
    amount: 480
  }];
  const stats = [{
    title: 'Drinks',
    value: '1,245',
    change: '+12%',
    icon: DropletIcon,
    color: '#00E4BB'
  }, {
    title: 'Users',
    value: '328',
    change: '+8%',
    icon: UsersIcon,
    color: '#A6FF4D'
  }, {
    title: 'Revenue',
    value: '$2,590',
    change: '+15%',
    icon: CreditCardIcon,
    color: '#FF9800'
  }, {
    title: 'Members',
    value: '85',
    change: '+5%',
    icon: CoffeeIcon,
    color: '#F06292'
  }];
  // Mock data for campaigns
  const campaigns = [{
    id: 1,
    title: 'Summer Hydration Challenge',
    status: 'Active',
    target: 'All Members',
    startDate: 'Jun 1, 2023',
    endDate: 'Aug 31, 2023',
    engagement: '32%'
  }, {
    id: 2,
    title: 'Double Points Weekends',
    status: 'Scheduled',
    target: 'Premium Members',
    startDate: 'Jul 15, 2023',
    endDate: 'Aug 15, 2023',
    engagement: '-'
  }, {
    id: 3,
    title: 'New Flavor Launch: Tropical Breeze',
    status: 'Ended',
    target: 'All Members',
    startDate: 'May 1, 2023',
    endDate: 'May 31, 2023',
    engagement: '47%'
  }];
  // Mock data for announcements
  const announcements = [{
    id: 1,
    title: 'New Berry Blast Flavor Now Available!',
    date: 'Jun 15, 2023',
    location: 'All Locations',
    status: 'Active'
  }, {
    id: 2,
    title: 'Maintenance: Soda Fountains Offline (2-4 AM)',
    date: 'Jun 20, 2023',
    location: 'Eco Café',
    status: 'Scheduled'
  }, {
    id: 3,
    title: 'Member Appreciation Day - Free Upgrades',
    date: 'Jun 10, 2023',
    location: 'All Locations',
    status: 'Ended'
  }];
  // Mock data for member activity
  const memberActivity = [{
    id: 1,
    name: 'Sarah K.',
    email: 'sarah.k@example.com',
    lastActive: 'Today, 10:25 AM',
    location: 'Eco Café',
    consumption: [{
      date: 'Jun 15',
      amount: 500,
      type: 'Berry Blast'
    }, {
      date: 'Jun 14',
      amount: 350,
      type: 'Sparkling Water'
    }, {
      date: 'Jun 13',
      amount: 500,
      type: 'Citrus Fizz'
    }],
    totalConsumption: '4.5L',
    membershipType: 'Premium'
  }, {
    id: 2,
    name: 'Mike R.',
    email: 'mike.r@example.com',
    lastActive: 'Today, 11:15 AM',
    location: 'Fitness Center',
    consumption: [{
      date: 'Jun 15',
      amount: 750,
      type: 'Sparkling Water'
    }, {
      date: 'Jun 14',
      amount: 750,
      type: 'Sparkling Water'
    }, {
      date: 'Jun 12',
      amount: 500,
      type: 'Regular Water'
    }],
    totalConsumption: '6.2L',
    membershipType: 'Basic'
  }, {
    id: 3,
    name: 'Emma W.',
    email: 'emma.w@example.com',
    lastActive: 'Yesterday, 3:45 PM',
    location: 'Hydration Lounge',
    consumption: [{
      date: 'Jun 14',
      amount: 350,
      type: 'Citrus Fizz'
    }, {
      date: 'Jun 13',
      amount: 350,
      type: 'Berry Blast'
    }, {
      date: 'Jun 12',
      amount: 350,
      type: 'Tropical Breeze'
    }],
    totalConsumption: '3.1L',
    membershipType: 'Premium'
  }];
  // Mock data for refill status
  const syrupInventory = [{
    id: 1,
    flavor: 'Berry Blast',
    level: 78,
    lastRefill: 'Jun 12, 2023',
    status: 'Good'
  }, {
    id: 2,
    flavor: 'Citrus Fizz',
    level: 45,
    lastRefill: 'Jun 10, 2023',
    status: 'Good'
  }, {
    id: 3,
    flavor: 'Tropical Breeze',
    level: 22,
    lastRefill: 'Jun 5, 2023',
    status: 'Low'
  }, {
    id: 4,
    flavor: 'Watermelon',
    level: 12,
    lastRefill: 'Jun 3, 2023',
    status: 'Critical'
  }, {
    id: 5,
    flavor: 'Green Apple',
    level: 65,
    lastRefill: 'Jun 8, 2023',
    status: 'Good'
  }, {
    id: 6,
    flavor: 'Peach Tea',
    level: 54,
    lastRefill: 'Jun 9, 2023',
    status: 'Good'
  }, {
    id: 7,
    flavor: 'Strawberry',
    level: 32,
    lastRefill: 'Jun 6, 2023',
    status: 'Low'
  }, {
    id: 8,
    flavor: 'Mango',
    level: 87,
    lastRefill: 'Jun 14, 2023',
    status: 'Good'
  }];
  const co2Status = {
    level: 68,
    lastReplaced: 'Jun 7, 2023',
    nextScheduled: 'Jun 21, 2023',
    status: 'Good'
  };
  const renderDashboard = () => <>
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-111">
        {stats.map((stat, index) => <GlassCard key={index} className="p-4" data-id="element-112">
            <div className="flex flex-col items-center text-center" data-id="element-113">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{
            backgroundColor: `${stat.color}20`
          }} data-id="element-114">
                <stat.icon className="w-5 h-5" style={{
              color: stat.color
            }} data-id="element-115" />
              </div>
              <h3 className="text-white font-medium text-xs mb-1" data-id="element-116">
                {stat.title}
              </h3>
              <p className="text-2xl font-bold text-white mb-1" data-id="element-117">{stat.value}</p>
              <p className="text-xs" style={{
            color: stat.color
          }} data-id="element-118">
                {stat.change}
              </p>
            </div>
          </GlassCard>)}
      </div>
      {/* Consumption Chart */}
      <GlassCard className="mb-6 p-4" data-id="element-119">
        <div className="flex justify-between items-center mb-4" data-id="element-120">
          <h2 className="text-lg font-bold text-white" data-id="element-121">Drink Consumption</h2>
          <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-122">
            <FilterIcon className="w-4 h-4 mr-1" data-id="element-123" /> Filter
          </Button>
        </div>
        <div className="h-64" data-id="element-124">
          <ResponsiveContainer width="100%" height="100%" data-id="element-125">
            <BarChart data={consumptionData} margin={{
            top: 5,
            right: 0,
            left: -20,
            bottom: 5
          }} data-id="element-126">
              <CartesianGrid strokeDasharray="3 3" stroke="#607D8B20" data-id="element-127" />
              <XAxis dataKey="name" tick={{
              fill: '#607D8B'
            }} data-id="element-128" />
              <YAxis tick={{
              fill: '#607D8B'
            }} data-id="element-129" />
              <Tooltip contentStyle={{
              backgroundColor: '#1A237E',
              borderColor: '#00E4BB30',
              color: 'white'
            }} data-id="element-130" />
              <Legend data-id="element-131" />
              <Bar dataKey="regular" name="Regular Water" fill="#00E4BB" radius={[4, 4, 0, 0]} data-id="element-132" />
              <Bar dataKey="sparkling" name="Sparkling" fill="#A6FF4D" radius={[4, 4, 0, 0]} data-id="element-133" />
              <Bar dataKey="flavor" name="Flavored" fill="#FF9800" radius={[4, 4, 0, 0]} data-id="element-134" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
      {/* User Activity Chart */}
      <GlassCard className="mb-6 p-4" data-id="element-135">
        <div className="flex justify-between items-center mb-4" data-id="element-136">
          <h2 className="text-lg font-bold text-white" data-id="element-137">User Activity</h2>
          <div className="flex items-center text-sm" data-id="element-138">
            <div className="flex items-center mr-3" data-id="element-139">
              <div className="w-3 h-3 rounded-full bg-[#00E4BB] mr-1" data-id="element-140"></div>
              <span className="text-gray-300" data-id="element-141">New Users</span>
            </div>
            <div className="flex items-center" data-id="element-142">
              <div className="w-3 h-3 rounded-full bg-[#A6FF4D] mr-1" data-id="element-143"></div>
              <span className="text-gray-300" data-id="element-144">Active Users</span>
            </div>
          </div>
        </div>
        <div className="h-64" data-id="element-145">
          <ResponsiveContainer width="100%" height="100%" data-id="element-146">
            <LineChart data={userActivityData} margin={{
            top: 5,
            right: 0,
            left: -20,
            bottom: 5
          }} data-id="element-147">
              <CartesianGrid strokeDasharray="3 3" stroke="#607D8B20" data-id="element-148" />
              <XAxis dataKey="name" tick={{
              fill: '#607D8B'
            }} data-id="element-149" />
              <YAxis tick={{
              fill: '#607D8B'
            }} data-id="element-150" />
              <Tooltip contentStyle={{
              backgroundColor: '#1A237E',
              borderColor: '#00E4BB30',
              color: 'white'
            }} data-id="element-151" />
              <Line type="monotone" dataKey="newUsers" name="New Users" stroke="#00E4BB" strokeWidth={2} dot={{
              r: 4,
              fill: '#00E4BB'
            }} activeDot={{
              r: 6
            }} data-id="element-152" />
              <Line type="monotone" dataKey="activeUsers" name="Active Users" stroke="#A6FF4D" strokeWidth={2} dot={{
              r: 4,
              fill: '#A6FF4D'
            }} activeDot={{
              r: 6
            }} data-id="element-153" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
      {/* Membership and Revenue */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-154">
        {/* Membership Distribution */}
        <GlassCard className="p-4" data-id="element-155">
          <h2 className="text-lg font-bold text-white mb-4" data-id="element-156">
            Membership Distribution
          </h2>
          <div className="h-64 flex items-center justify-center" data-id="element-157">
            <ResponsiveContainer width="100%" height="100%" data-id="element-158">
              <PieChart data-id="element-159">
                <Pie data={membershipData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                percent
              }) => `${name} ${(percent * 100).toFixed(0)}%`} data-id="element-160">
                  {membershipData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-161" />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#1A237E',
                borderColor: '#00E4BB30',
                color: 'white'
              }} data-id="element-162" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
        {/* Revenue Chart */}
        <GlassCard className="p-4" data-id="element-163">
          <h2 className="text-lg font-bold text-white mb-4" data-id="element-164">Weekly Revenue</h2>
          <div className="h-64" data-id="element-165">
            <ResponsiveContainer width="100%" height="100%" data-id="element-166">
              <BarChart data={revenueData} margin={{
              top: 5,
              right: 0,
              left: -20,
              bottom: 5
            }} data-id="element-167">
                <CartesianGrid strokeDasharray="3 3" stroke="#607D8B20" data-id="element-168" />
                <XAxis dataKey="name" tick={{
                fill: '#607D8B'
              }} data-id="element-169" />
                <YAxis tick={{
                fill: '#607D8B'
              }} data-id="element-170" />
                <Tooltip contentStyle={{
                backgroundColor: '#1A237E',
                borderColor: '#00E4BB30',
                color: 'white'
              }} formatter={value => [`$${value}`, 'Revenue']} data-id="element-171" />
                <Bar dataKey="amount" name="Revenue" fill="#FF9800" radius={[4, 4, 0, 0]} data-id="element-172" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>
      {/* Recent Transactions */}
      <GlassCard className="mb-6 p-4" data-id="element-173">
        <h2 className="text-lg font-bold text-white mb-4" data-id="element-174">
          Recent Transactions
        </h2>
        <div className="overflow-x-auto" data-id="element-175">
          <table className="min-w-full divide-y divide-[#00E4BB]/20" data-id="element-176">
            <thead data-id="element-177">
              <tr data-id="element-178">
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-179">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-180">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-181">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-182">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-183">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#00E4BB]/10" data-id="element-184">
              {[{
              id: 1,
              user: 'Sarah K.',
              product: 'Berry Blast',
              time: '10:25 AM',
              amount: '$4.99',
              status: 'Completed'
            }, {
              id: 2,
              user: 'Mike R.',
              product: 'Sparkling Water',
              time: '11:15 AM',
              amount: '$3.50',
              status: 'Completed'
            }, {
              id: 3,
              user: 'John D.',
              product: 'Hydration Club',
              time: '12:30 PM',
              amount: '$29.99',
              status: 'Pending'
            }, {
              id: 4,
              user: 'Emma W.',
              product: 'Citrus Fizz',
              time: '1:45 PM',
              amount: '$4.50',
              status: 'Completed'
            }, {
              id: 5,
              user: 'Alex B.',
              product: 'Smart Tumbler',
              time: '2:20 PM',
              amount: '$19.99',
              status: 'Completed'
            }].map(transaction => <tr key={transaction.id} data-id="element-185">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white" data-id="element-186">
                    {transaction.user}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white" data-id="element-187">
                    {transaction.product}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300" data-id="element-188">
                    {transaction.time}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white" data-id="element-189">
                    {transaction.amount}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-190">
                    <span className={`px-2 py-1 rounded-full text-xs ${transaction.status === 'Completed' ? 'bg-[#A6FF4D]/20 text-[#A6FF4D]' : 'bg-[#FF9800]/20 text-[#FF9800]'}`} data-id="element-191">
                      {transaction.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </>;
  const renderCampaigns = () => <>
      <div className="flex justify-between items-center mb-6" data-id="element-192">
        <h2 className="text-xl font-bold text-white" data-id="element-193">Campaigns</h2>
        <Button className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowCampaignForm(true)} data-id="element-194">
          <PlusIcon className="w-4 h-4 mr-1" data-id="element-195" /> New
        </Button>
      </div>
      {/* Campaign Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-196">
        <GlassCard className="p-4" data-id="element-197">
          <div className="flex flex-col items-center text-center" data-id="element-198">
            <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mb-2" data-id="element-199">
              <TargetIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-200" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-201">Active</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-202">3</p>
            <div className="text-xs text-[#00E4BB]" data-id="element-203">+1 this month</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-204">
          <div className="flex flex-col items-center text-center" data-id="element-205">
            <div className="w-10 h-10 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mb-2" data-id="element-206">
              <UsersIcon className="w-5 h-5 text-[#A6FF4D]" data-id="element-207" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-208">Engagement</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-209">34%</p>
            <div className="text-xs text-[#A6FF4D]" data-id="element-210">+5% this month</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-211">
          <div className="flex flex-col items-center text-center" data-id="element-212">
            <div className="w-10 h-10 rounded-full bg-[#FF9800]/20 flex items-center justify-center mb-2" data-id="element-213">
              <TrendingUpIcon className="w-5 h-5 text-[#FF9800]" data-id="element-214" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-215">Conversion</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-216">12.5%</p>
            <div className="text-xs text-[#FF9800]" data-id="element-217">+2.1% this month</div>
          </div>
        </GlassCard>
      </div>
      {/* Active Campaigns */}
      <GlassCard className="mb-6 p-4" data-id="element-218">
        <div className="flex justify-between items-center mb-4" data-id="element-219">
          <h3 className="text-lg font-bold text-white" data-id="element-220">Current Campaigns</h3>
          <div className="flex space-x-2" data-id="element-221">
            <div className="relative" data-id="element-222">
              <input type="text" placeholder="Search campaigns..." className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg pl-9 pr-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB] w-64" data-id="element-223" />
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#00E4BB]" data-id="element-224" />
            </div>
            <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-225">
              <FilterIcon className="w-4 h-4 mr-1" data-id="element-226" /> Filter
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-227">
          <table className="min-w-full divide-y divide-[#00E4BB]/20" data-id="element-228">
            <thead data-id="element-229">
              <tr data-id="element-230">
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-231">
                  Campaign
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-232">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-233">
                  Target
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-234">
                  Start Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-235">
                  End Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-236">
                  Engagement
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-237">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#00E4BB]/10" data-id="element-238">
              {campaigns.map(campaign => <tr key={campaign.id} data-id="element-239">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white" data-id="element-240">
                    {campaign.title}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-241">
                    <span className={`px-2 py-1 rounded-full text-xs ${campaign.status === 'Active' ? 'bg-[#A6FF4D]/20 text-[#A6FF4D]' : campaign.status === 'Scheduled' ? 'bg-[#00E4BB]/20 text-[#00E4BB]' : 'bg-gray-500/20 text-gray-400'}`} data-id="element-242">
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300" data-id="element-243">
                    {campaign.target}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300" data-id="element-244">
                    {campaign.startDate}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300" data-id="element-245">
                    {campaign.endDate}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white font-medium" data-id="element-246">
                    {campaign.engagement}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-247">
                    <div className="flex space-x-2" data-id="element-248">
                      <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB]" data-id="element-249">
                        <PlusIcon className="w-4 h-4" data-id="element-250" />
                      </button>
                      <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB]" data-id="element-251">
                        <BarChart3Icon className="w-4 h-4" data-id="element-252" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </GlassCard>
      {/* Campaign Ideas */}
      <GlassCard className="mb-6 p-4" data-id="element-253">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-254">Campaign Ideas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-255">
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-256">
            <div className="flex items-center mb-3" data-id="element-257">
              <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-258">
                <DropletIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-259" />
              </div>
              <h4 className="text-white font-medium" data-id="element-260">Hydration Challenge</h4>
            </div>
            <p className="text-gray-300 text-sm mb-3" data-id="element-261">
              Encourage users to meet daily hydration goals for a week to earn
              bonus points and rewards.
            </p>
            <div className="flex justify-end" data-id="element-262">
              <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-263">
                Use Template
              </Button>
            </div>
          </div>
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-264">
            <div className="flex items-center mb-3" data-id="element-265">
              <div className="w-10 h-10 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mr-3" data-id="element-266">
                <UsersIcon className="w-5 h-5 text-[#A6FF4D]" data-id="element-267" />
              </div>
              <h4 className="text-white font-medium" data-id="element-268">Refer-a-Friend</h4>
            </div>
            <p className="text-gray-300 text-sm mb-3" data-id="element-269">
              Reward members who invite friends to join Smart Soda with
              exclusive flavors and discounts.
            </p>
            <div className="flex justify-end" data-id="element-270">
              <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-271">
                Use Template
              </Button>
            </div>
          </div>
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-272">
            <div className="flex items-center mb-3" data-id="element-273">
              <div className="w-10 h-10 rounded-full bg-[#FF9800]/20 flex items-center justify-center mr-3" data-id="element-274">
                <CoffeeIcon className="w-5 h-5 text-[#FF9800]" data-id="element-275" />
              </div>
              <h4 className="text-white font-medium" data-id="element-276">Flavor of the Month</h4>
            </div>
            <p className="text-gray-300 text-sm mb-3" data-id="element-277">
              Promote a new or seasonal flavor with special pricing and
              limited-time rewards.
            </p>
            <div className="flex justify-end" data-id="element-278">
              <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-279">
                Use Template
              </Button>
            </div>
          </div>
        </div>
      </GlassCard>
      {/* Campaign Creation Form Modal */}
      {showCampaignForm && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-280">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8" data-id="element-281">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-[#1A237E] z-10 py-2" data-id="element-282">
              <h3 className="text-xl font-bold text-white" data-id="element-283">
                Create New Campaign
              </h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowCampaignForm(false)} aria-label="Close" type="button" data-id="element-284">
                <XIcon className="w-5 h-5" data-id="element-285" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-286">
              <div data-id="element-287">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-288">
                  Campaign Name
                </label>
                <input type="text" className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" placeholder="Enter campaign name" data-id="element-289" />
              </div>
              <div data-id="element-290">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-291">
                  Target Audience
                </label>
                <select className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-292">
                  <option value="" data-id="element-293">Select target audience</option>
                  <option value="all" data-id="element-294">All Members</option>
                  <option value="premium" data-id="element-295">Premium Members</option>
                  <option value="basic" data-id="element-296">Basic Members</option>
                  <option value="inactive" data-id="element-297">Inactive Members</option>
                </select>
              </div>
              <div data-id="element-298">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-299">
                  Start Date
                </label>
                <input type="date" className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-300" />
              </div>
              <div data-id="element-301">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-302">
                  End Date
                </label>
                <input type="date" className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-303" />
              </div>
              <div className="col-span-2" data-id="element-304">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-305">
                  Campaign Type
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-306">
                  <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-3 cursor-pointer hover:bg-[#00E4BB]/10" data-id="element-307">
                    <div className="flex items-center mb-2" data-id="element-308">
                      <div className="w-8 h-8 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-2" data-id="element-309">
                        <DropletIcon className="w-4 h-4 text-[#00E4BB]" data-id="element-310" />
                      </div>
                      <span className="text-white text-sm" data-id="element-311">
                        Hydration Challenge
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs" data-id="element-312">
                      Daily goals and rewards
                    </p>
                  </div>
                  <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-3 cursor-pointer hover:bg-[#00E4BB]/10" data-id="element-313">
                    <div className="flex items-center mb-2" data-id="element-314">
                      <div className="w-8 h-8 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mr-2" data-id="element-315">
                        <UsersIcon className="w-4 h-4 text-[#A6FF4D]" data-id="element-316" />
                      </div>
                      <span className="text-white text-sm" data-id="element-317">
                        Referral Program
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs" data-id="element-318">
                      Invite friends for rewards
                    </p>
                  </div>
                  <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-3 cursor-pointer hover:bg-[#00E4BB]/10" data-id="element-319">
                    <div className="flex items-center mb-2" data-id="element-320">
                      <div className="w-8 h-8 rounded-full bg-[#FF9800]/20 flex items-center justify-center mr-2" data-id="element-321">
                        <CoffeeIcon className="w-4 h-4 text-[#FF9800]" data-id="element-322" />
                      </div>
                      <span className="text-white text-sm" data-id="element-323">
                        New Flavor Promo
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs" data-id="element-324">
                      Promote a specific flavor
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-span-2" data-id="element-325">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-326">
                  Campaign Description
                </label>
                <textarea className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB] h-24" placeholder="Describe your campaign..." data-id="element-327"></textarea>
              </div>
              <div className="col-span-2" data-id="element-328">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-329">
                  Reward Structure
                </label>
                <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-330">
                  <div className="flex items-center justify-between mb-2" data-id="element-331">
                    <span className="text-white text-sm" data-id="element-332">
                      Free drink after 5 check-ins
                    </span>
                    <button className="text-[#00E4BB]" data-id="element-333">
                      <XIcon className="w-4 h-4" data-id="element-334" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mb-4" data-id="element-335">
                    <span className="text-white text-sm" data-id="element-336">
                      Double points for new flavor tries
                    </span>
                    <button className="text-[#00E4BB]" data-id="element-337">
                      <XIcon className="w-4 h-4" data-id="element-338" />
                    </button>
                  </div>
                  <button className="text-[#00E4BB] text-sm flex items-center" data-id="element-339">
                    <PlusIcon className="w-4 h-4 mr-1" data-id="element-340" /> Add Reward
                  </button>
                </div>
              </div>
              <div className="col-span-2" data-id="element-341">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-342">
                  Notification Preview
                </label>
                <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-343">
                  <div className="flex items-start" data-id="element-344">
                    <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-345">
                      <DropletIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-346" />
                    </div>
                    <div data-id="element-347">
                      <h4 className="text-white font-medium" data-id="element-348">
                        Summer Hydration Challenge
                      </h4>
                      <p className="text-gray-300 text-sm mb-2" data-id="element-349">
                        Join our Summer Hydration Challenge! Meet your daily
                        goals and earn exclusive rewards.
                      </p>
                      <div className="flex space-x-2" data-id="element-350">
                        <button className="text-xs bg-[#00E4BB] text-[#1A237E] px-2 py-1 rounded" data-id="element-351">
                          Join Now
                        </button>
                        <button className="text-xs bg-transparent border border-[#00E4BB]/30 text-[#00E4BB] px-2 py-1 rounded" data-id="element-352">
                          Learn More
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6 sticky bottom-0 bg-[#1A237E] py-3 z-10" data-id="element-353">
              <Button variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" onClick={() => setShowCampaignForm(false)} data-id="element-354">
                Cancel
              </Button>
              <Button className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowCampaignForm(false)} data-id="element-355">
                Create Campaign
              </Button>
            </div>
          </div>
        </div>}
    </>;
  const renderAnnouncements = () => <>
      <div className="flex justify-between items-center mb-6" data-id="element-356">
        <h2 className="text-xl font-bold text-white" data-id="element-357">Announcements</h2>
        <Button className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowAnnouncementForm(true)} data-id="element-358">
          <PlusIcon className="w-4 h-4 mr-1" data-id="element-359" /> New
        </Button>
      </div>
      {/* Announcement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-360">
        <GlassCard className="p-4" data-id="element-361">
          <div className="flex flex-col items-center text-center" data-id="element-362">
            <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mb-2" data-id="element-363">
              <BellIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-364" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-365">Active</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-366">2</p>
            <div className="text-xs text-[#00E4BB]" data-id="element-367">1 scheduled</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-368">
          <div className="flex flex-col items-center text-center" data-id="element-369">
            <div className="w-10 h-10 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mb-2" data-id="element-370">
              <UsersIcon className="w-5 h-5 text-[#A6FF4D]" data-id="element-371" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-372">Read Rate</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-373">76%</p>
            <div className="text-xs text-[#A6FF4D]" data-id="element-374">+12% this month</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-375">
          <div className="flex flex-col items-center text-center" data-id="element-376">
            <div className="w-10 h-10 rounded-full bg-[#FF9800]/20 flex items-center justify-center mb-2" data-id="element-377">
              <MegaphoneIcon className="w-5 h-5 text-[#FF9800]" data-id="element-378" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-379">Click Rate</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-380">38%</p>
            <div className="text-xs text-[#FF9800]" data-id="element-381">+5% this month</div>
          </div>
        </GlassCard>
      </div>
      {/* Current Announcements */}
      <GlassCard className="mb-6 p-4" data-id="element-382">
        <div className="flex justify-between items-center mb-4" data-id="element-383">
          <h3 className="text-lg font-bold text-white" data-id="element-384">
            Current Announcements
          </h3>
          <div className="flex space-x-2" data-id="element-385">
            <select className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-386">
              <option value="all" data-id="element-387">All Locations</option>
              <option value="eco" data-id="element-388">Eco Café</option>
              <option value="fitness" data-id="element-389">Fitness Center</option>
              <option value="lounge" data-id="element-390">Hydration Lounge</option>
            </select>
            <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-391">
              <FilterIcon className="w-4 h-4 mr-1" data-id="element-392" /> Filter
            </Button>
          </div>
        </div>
        <div className="space-y-4" data-id="element-393">
          {announcements.map(announcement => <div key={announcement.id} className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-394">
              <div className="flex justify-between items-start" data-id="element-395">
                <div className="flex items-start" data-id="element-396">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3 mt-1" data-id="element-397">
                    <BellIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-398" />
                  </div>
                  <div data-id="element-399">
                    <h4 className="text-white font-medium" data-id="element-400">
                      {announcement.title}
                    </h4>
                    <div className="flex items-center text-sm text-gray-300 mt-1" data-id="element-401">
                      <CalendarIcon className="w-3 h-3 mr-1" data-id="element-402" />{' '}
                      {announcement.date}
                      <span className="mx-2" data-id="element-403">•</span>
                      <MapPinIcon className="w-3 h-3 mr-1" data-id="element-404" />{' '}
                      {announcement.location}
                    </div>
                    <div className="mt-3 flex space-x-2" data-id="element-405">
                      <span className={`px-2 py-1 rounded-full text-xs ${announcement.status === 'Active' ? 'bg-[#A6FF4D]/20 text-[#A6FF4D]' : announcement.status === 'Scheduled' ? 'bg-[#00E4BB]/20 text-[#00E4BB]' : 'bg-gray-500/20 text-gray-400'}`} data-id="element-406">
                        {announcement.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2" data-id="element-407">
                  <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB]" data-id="element-408">
                    <PlusIcon className="w-4 h-4" data-id="element-409" />
                  </button>
                  <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB]" data-id="element-410">
                    <BarChart3Icon className="w-4 h-4" data-id="element-411" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </GlassCard>
      {/* Announcement Templates */}
      <GlassCard className="mb-6 p-4" data-id="element-412">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-413">
          Announcement Templates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-414">
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-415">
            <div className="flex items-center mb-3" data-id="element-416">
              <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-417">
                <DropletIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-418" />
              </div>
              <h4 className="text-white font-medium" data-id="element-419">New Flavor Launch</h4>
            </div>
            <p className="text-gray-300 text-sm mb-3" data-id="element-420">
              Announce a new flavor with details about taste profile and
              availability.
            </p>
            <div className="flex justify-end" data-id="element-421">
              <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-422">
                Use Template
              </Button>
            </div>
          </div>
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-423">
            <div className="flex items-center mb-3" data-id="element-424">
              <div className="w-10 h-10 rounded-full bg-[#FF9800]/20 flex items-center justify-center mr-3" data-id="element-425">
                <CalendarIcon className="w-5 h-5 text-[#FF9800]" data-id="element-426" />
              </div>
              <h4 className="text-white font-medium" data-id="element-427">Special Event</h4>
            </div>
            <p className="text-gray-300 text-sm mb-3" data-id="element-428">
              Promote an upcoming event with time, location, and special
              offerings.
            </p>
            <div className="flex justify-end" data-id="element-429">
              <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-430">
                Use Template
              </Button>
            </div>
          </div>
        </div>
      </GlassCard>
      {/* Announcement Form Modal */}
      {showAnnouncementForm && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-431">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-lg" data-id="element-432">
            <div className="flex justify-between items-center mb-4" data-id="element-433">
              <h3 className="text-xl font-bold text-white" data-id="element-434">
                Create Announcement
              </h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowAnnouncementForm(false)} data-id="element-435">
                <XIcon className="w-5 h-5" data-id="element-436" />
              </button>
            </div>
            <div className="space-y-4 mb-4" data-id="element-437">
              <div data-id="element-438">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-439">
                  Announcement Title
                </label>
                <input type="text" className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" placeholder="Enter announcement title" data-id="element-440" />
              </div>
              <div data-id="element-441">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-442">
                  Location
                </label>
                <select className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-443">
                  <option value="" data-id="element-444">Select location</option>
                  <option value="all" data-id="element-445">All Locations</option>
                  <option value="eco" data-id="element-446">Eco Café</option>
                  <option value="fitness" data-id="element-447">Fitness Center</option>
                  <option value="lounge" data-id="element-448">Hydration Lounge</option>
                </select>
              </div>
              <div data-id="element-449">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-450">
                  Date
                </label>
                <input type="date" className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-451" />
              </div>
              <div data-id="element-452">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-453">
                  Announcement Type
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-454">
                  <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-2 cursor-pointer hover:bg-[#00E4BB]/10 flex flex-col items-center" data-id="element-455">
                    <DropletIcon className="w-5 h-5 text-[#00E4BB] mb-1" data-id="element-456" />
                    <span className="text-white text-xs" data-id="element-457">New Flavor</span>
                  </div>
                  <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-2 cursor-pointer hover:bg-[#00E4BB]/10 flex flex-col items-center" data-id="element-458">
                    <CalendarIcon className="w-5 h-5 text-[#FF9800] mb-1" data-id="element-459" />
                    <span className="text-white text-xs" data-id="element-460">Event</span>
                  </div>
                  <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-2 cursor-pointer hover:bg-[#00E4BB]/10 flex flex-col items-center" data-id="element-461">
                    <BellIcon className="w-5 h-5 text-[#A6FF4D] mb-1" data-id="element-462" />
                    <span className="text-white text-xs" data-id="element-463">Update</span>
                  </div>
                </div>
              </div>
              <div data-id="element-464">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-465">
                  Announcement Content
                </label>
                <textarea className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB] h-24" placeholder="Enter announcement details..." data-id="element-466"></textarea>
              </div>
              <div data-id="element-467">
                <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-468">
                  Add Image (optional)
                </label>
                <div className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 border-dashed rounded-lg p-4 flex flex-col items-center justify-center" data-id="element-469">
                  <ImageIcon className="w-8 h-8 text-[#00E4BB]/50 mb-2" data-id="element-470" />
                  <p className="text-gray-400 text-sm" data-id="element-471">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-gray-500 text-xs mt-1" data-id="element-472">
                    PNG, JPG, GIF up to 2MB
                  </p>
                </div>
              </div>
              <div className="flex items-center" data-id="element-473">
                <input type="checkbox" id="push-notification" className="w-4 h-4 text-[#00E4BB] bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded focus:ring-[#00E4BB]" data-id="element-474" />
                <label htmlFor="push-notification" className="ml-2 text-sm text-white" data-id="element-475">
                  Send as push notification
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-476">
              <Button variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" onClick={() => setShowAnnouncementForm(false)} data-id="element-477">
                Cancel
              </Button>
              <Button className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowAnnouncementForm(false)} data-id="element-478">
                Publish Announcement
              </Button>
            </div>
          </div>
        </div>}
    </>;
  const renderMemberActivity = () => <>
      <div className="flex justify-between items-center mb-6" data-id="element-479">
        <h2 className="text-xl font-bold text-white" data-id="element-480">Members</h2>
        <div className="flex space-x-2" data-id="element-481">
          <div className="relative" data-id="element-482">
            <input type="text" placeholder="Search..." className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg pl-9 pr-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB] w-40" data-id="element-483" />
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#00E4BB]" data-id="element-484" />
          </div>
          <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-485">
            <FilterIcon className="w-4 h-4 mr-1" data-id="element-486" /> Filter
          </Button>
          <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-487">
            <DownloadIcon className="w-4 h-4 mr-1" data-id="element-488" /> Export
          </Button>
        </div>
      </div>
      {/* Activity Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-489">
        <GlassCard className="p-4" data-id="element-490">
          <div className="flex flex-col items-center text-center" data-id="element-491">
            <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mb-2" data-id="element-492">
              <UsersIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-493" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-494">Active Today</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-495">87</p>
            <div className="text-xs text-[#00E4BB]" data-id="element-496">+12 today</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-497">
          <div className="flex flex-col items-center text-center" data-id="element-498">
            <div className="w-10 h-10 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mb-2" data-id="element-499">
              <DropletIcon className="w-5 h-5 text-[#A6FF4D]" data-id="element-500" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-501">Total</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-502">156.8L</p>
            <div className="text-xs text-[#A6FF4D]" data-id="element-503">+22.3L today</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-504">
          <div className="flex flex-col items-center text-center" data-id="element-505">
            <div className="w-10 h-10 rounded-full bg-[#FF9800]/20 flex items-center justify-center mb-2" data-id="element-506">
              <TrendingUpIcon className="w-5 h-5 text-[#FF9800]" data-id="element-507" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-508">Avg. Session</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-509">3.2 min</p>
            <div className="text-xs text-[#FF9800]" data-id="element-510">+0.5 min</div>
          </div>
        </GlassCard>
        <GlassCard className="p-4" data-id="element-511">
          <div className="flex flex-col items-center text-center" data-id="element-512">
            <div className="w-10 h-10 rounded-full bg-[#F06292]/20 flex items-center justify-center mb-2" data-id="element-513">
              <UserIcon className="w-5 h-5 text-[#F06292]" data-id="element-514" />
            </div>
            <p className="text-white text-xs mb-1" data-id="element-515">New</p>
            <p className="text-2xl font-bold text-white mb-1" data-id="element-516">12</p>
            <div className="text-xs text-[#F06292]" data-id="element-517">+5 today</div>
          </div>
        </GlassCard>
      </div>
      {/* Member Activity List */}
      <GlassCard className="mb-6 p-4" data-id="element-518">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-519">
          Member Activity Feed
        </h3>
        <div className="space-y-4" data-id="element-520">
          {memberActivity.map(member => <div key={member.id} className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-521">
              <div className="flex justify-between" data-id="element-522">
                <div className="flex items-start" data-id="element-523">
                  <div className="w-12 h-12 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-524">
                    <UserIcon className="w-6 h-6 text-[#00E4BB]" data-id="element-525" />
                  </div>
                  <div data-id="element-526">
                    <div className="flex items-center" data-id="element-527">
                      <h4 className="text-white font-medium" data-id="element-528">{member.name}</h4>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${member.membershipType === 'Premium' ? 'bg-[#FF9800]/20 text-[#FF9800]' : 'bg-[#00E4BB]/20 text-[#00E4BB]'}`} data-id="element-529">
                        {member.membershipType}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm" data-id="element-530">{member.email}</p>
                    <div className="flex items-center text-sm text-gray-300 mt-1" data-id="element-531">
                      <ClockIcon className="w-3 h-3 mr-1" data-id="element-532" /> {member.lastActive}
                      <span className="mx-2" data-id="element-533">•</span>
                      <MapPinIcon className="w-3 h-3 mr-1" data-id="element-534" /> {member.location}
                    </div>
                    <div className="mt-3" data-id="element-535">
                      <p className="text-[#00E4BB] text-sm font-medium mb-2" data-id="element-536">
                        Recent Activity
                      </p>
                      <div className="space-y-2" data-id="element-537">
                        {member.consumption.map((item, idx) => <div key={idx} className="flex items-center text-sm" data-id="element-538">
                            <div className="w-2 h-2 rounded-full bg-[#00E4BB] mr-2" data-id="element-539"></div>
                            <span className="text-gray-300" data-id="element-540">{item.date}:</span>
                            <span className="text-white ml-2" data-id="element-541">
                              {item.amount}ml {item.type}
                            </span>
                          </div>)}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center" data-id="element-542">
                      <span className="text-white text-sm mr-2" data-id="element-543">
                        Total Consumption:
                      </span>
                      <span className="text-[#A6FF4D] font-medium" data-id="element-544">
                        {member.totalConsumption}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex" data-id="element-545">
                  <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB] h-8 w-8 flex items-center justify-center" data-id="element-546">
                    <BarChart3Icon className="w-4 h-4" data-id="element-547" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </GlassCard>
      {/* IoT Connection Status */}
      <GlassCard className="mb-6 p-4" data-id="element-548">
        <div className="flex justify-between items-center mb-4" data-id="element-549">
          <h3 className="text-lg font-bold text-white" data-id="element-550">
            IoT Connection Status
          </h3>
          <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-551">
            <RefreshCwIcon className="w-4 h-4 mr-1" data-id="element-552" /> Refresh
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-553">
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-554">
            <div className="flex items-center justify-between mb-3" data-id="element-555">
              <div className="flex items-center" data-id="element-556">
                <div className="w-8 h-8 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mr-2" data-id="element-557">
                  <DropletIcon className="w-4 h-4 text-[#A6FF4D]" data-id="element-558" />
                </div>
                <h4 className="text-white font-medium" data-id="element-559">Eco Café</h4>
              </div>
              <span className="px-2 py-1 rounded-full text-xs bg-[#A6FF4D]/20 text-[#A6FF4D]" data-id="element-560">
                Online
              </span>
            </div>
            <p className="text-gray-300 text-sm mb-2" data-id="element-561">
              Last sync: 2 minutes ago
            </p>
            <p className="text-white text-sm" data-id="element-562">12 active devices</p>
          </div>
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-563">
            <div className="flex items-center justify-between mb-3" data-id="element-564">
              <div className="flex items-center" data-id="element-565">
                <div className="w-8 h-8 rounded-full bg-[#A6FF4D]/20 flex items-center justify-center mr-2" data-id="element-566">
                  <DropletIcon className="w-4 h-4 text-[#A6FF4D]" data-id="element-567" />
                </div>
                <h4 className="text-white font-medium" data-id="element-568">Fitness Center</h4>
              </div>
              <span className="px-2 py-1 rounded-full text-xs bg-[#A6FF4D]/20 text-[#A6FF4D]" data-id="element-569">
                Online
              </span>
            </div>
            <p className="text-gray-300 text-sm mb-2" data-id="element-570">
              Last sync: 5 minutes ago
            </p>
            <p className="text-white text-sm" data-id="element-571">8 active devices</p>
          </div>
          <div className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-4" data-id="element-572">
            <div className="flex items-center justify-between mb-3" data-id="element-573">
              <div className="flex items-center" data-id="element-574">
                <div className="w-8 h-8 rounded-full bg-[#FF9800]/20 flex items-center justify-center mr-2" data-id="element-575">
                  <DropletIcon className="w-4 h-4 text-[#FF9800]" data-id="element-576" />
                </div>
                <h4 className="text-white font-medium" data-id="element-577">Hydration Lounge</h4>
              </div>
              <span className="px-2 py-1 rounded-full text-xs bg-[#FF9800]/20 text-[#FF9800]" data-id="element-578">
                Maintenance
              </span>
            </div>
            <p className="text-gray-300 text-sm mb-2" data-id="element-579">
              Last sync: 45 minutes ago
            </p>
            <p className="text-white text-sm" data-id="element-580">4 active devices</p>
          </div>
        </div>
      </GlassCard>
    </>;
  const renderRefillStatus = () => <>
      <div className="flex justify-between items-center mb-6" data-id="element-581">
        <h2 className="text-xl font-bold text-white" data-id="element-582">Refill Status</h2>
        <div className="flex space-x-2" data-id="element-583">
          <select className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-584">
            <option value="all" data-id="element-585">All Locations</option>
            <option value="eco" data-id="element-586">Eco Café</option>
            <option value="fitness" data-id="element-587">Fitness Center</option>
            <option value="lounge" data-id="element-588">Hydration Lounge</option>
          </select>
          <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-589">
            <RefreshCwIcon className="w-4 h-4 mr-1" data-id="element-590" /> Refresh
          </Button>
        </div>
      </div>
      {/* CO2 Status */}
      <GlassCard className="mb-6 p-4" data-id="element-591">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-592">CO2 Status</h3>
        <div className="flex flex-col md:flex-row md:items-center" data-id="element-593">
          <div className="flex-1 mb-4 md:mb-0" data-id="element-594">
            <div className="flex items-center mb-3" data-id="element-595">
              <div className="w-12 h-12 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-4" data-id="element-596">
                <DropletIcon className="w-6 h-6 text-[#00E4BB]" data-id="element-597" />
              </div>
              <div data-id="element-598">
                <h4 className="text-white font-medium" data-id="element-599">CO2 Container</h4>
                <p className="text-gray-300 text-sm" data-id="element-600">
                  Last replaced: {co2Status.lastReplaced}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm ml-16" data-id="element-601">
              <ClockIcon className="w-4 h-4 text-[#00E4BB] mr-2" data-id="element-602" />
              <span className="text-gray-300" data-id="element-603">
                Next scheduled: {co2Status.nextScheduled}
              </span>
            </div>
          </div>
          <div className="flex-1" data-id="element-604">
            <div className="flex items-center justify-between mb-2" data-id="element-605">
              <span className="text-white" data-id="element-606">Current Level</span>
              <span className={`font-medium ${co2Status.level > 60 ? 'text-[#A6FF4D]' : co2Status.level > 30 ? 'text-[#FF9800]' : 'text-[#F44336]'}`} data-id="element-607">
                {co2Status.level}%
              </span>
            </div>
            <div className="h-4 bg-[#1A237E]/60 rounded-full overflow-hidden" data-id="element-608">
              <div className={`h-full ${co2Status.level > 60 ? 'bg-[#A6FF4D]' : co2Status.level > 30 ? 'bg-[#FF9800]' : 'bg-[#F44336]'}`} style={{
              width: `${co2Status.level}%`
            }} data-id="element-609"></div>
            </div>
            <div className="flex justify-between mt-2" data-id="element-610">
              <span className="text-gray-400 text-xs" data-id="element-611">0%</span>
              <span className="text-gray-400 text-xs" data-id="element-612">50%</span>
              <span className="text-gray-400 text-xs" data-id="element-613">100%</span>
            </div>
          </div>
        </div>
      </GlassCard>
      {/* Syrup Inventory */}
      <GlassCard className="mb-6 p-4" data-id="element-614">
        <div className="flex justify-between items-center mb-4" data-id="element-615">
          <h3 className="text-lg font-bold text-white" data-id="element-616">Syrup Inventory</h3>
          <div className="flex space-x-2" data-id="element-617">
            <div className="relative" data-id="element-618">
              <input type="text" placeholder="Search flavors..." className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg pl-9 pr-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB] w-48" data-id="element-619" />
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#00E4BB]" data-id="element-620" />
            </div>
            <Button size="sm" variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" data-id="element-621">
              <PlusIcon className="w-4 h-4 mr-1" data-id="element-622" /> Add Refill
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-623">
          <table className="min-w-full divide-y divide-[#00E4BB]/20" data-id="element-624">
            <thead data-id="element-625">
              <tr data-id="element-626">
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-627">
                  Flavor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-628">
                  Level
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-629">
                  Last Refill
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-630">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#00E4BB] uppercase tracking-wider" data-id="element-631">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#00E4BB]/10" data-id="element-632">
              {syrupInventory.map(syrup => <tr key={syrup.id} data-id="element-633">
                  <td className="px-4 py-3 whitespace-nowrap" data-id="element-634">
                    <div className="flex items-center" data-id="element-635">
                      <div className="w-8 h-8 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-636">
                        <DropletIcon className="w-4 h-4 text-[#00E4BB]" data-id="element-637" />
                      </div>
                      <span className="text-white" data-id="element-638">{syrup.flavor}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-id="element-639">
                    <div className="flex items-center" data-id="element-640">
                      <div className="w-20 h-2 bg-[#1A237E]/60 rounded-full overflow-hidden mr-2" data-id="element-641">
                        <div className={`h-full ${syrup.level > 60 ? 'bg-[#A6FF4D]' : syrup.level > 30 ? 'bg-[#FF9800]' : 'bg-[#F44336]'}`} style={{
                      width: `${syrup.level}%`
                    }} data-id="element-642"></div>
                      </div>
                      <span className="text-white" data-id="element-643">{syrup.level}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300" data-id="element-644">
                    {syrup.lastRefill}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-645">
                    <span className={`px-2 py-1 rounded-full text-xs ${syrup.status === 'Good' ? 'bg-[#A6FF4D]/20 text-[#A6FF4D]' : syrup.status === 'Low' ? 'bg-[#FF9800]/20 text-[#FF9800]' : 'bg-[#F44336]/20 text-[#F44336]'}`} data-id="element-646">
                      {syrup.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-647">
                    <div className="flex space-x-2" data-id="element-648">
                      <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB]" data-id="element-649">
                        <PlusIcon className="w-4 h-4" data-id="element-650" />
                      </button>
                      <button className="p-1 bg-[#00E4BB]/20 rounded text-[#00E4BB]" data-id="element-651">
                        <ChevronRightIcon className="w-4 h-4" data-id="element-652" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </GlassCard>
      {/* All Flavors Grid */}
      <GlassCard className="mb-6 p-4" data-id="element-653">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-654">All Flavors (26)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-655">
          {['Berry Blast', 'Citrus Fizz', 'Tropical Breeze', 'Watermelon', 'Green Apple', 'Peach Tea', 'Strawberry', 'Mango', 'Lemon Lime', 'Cherry', 'Blueberry', 'Raspberry', 'Orange', 'Grape', 'Pomegranate', 'Coconut', 'Vanilla', 'Mint', 'Cucumber', 'Ginger', 'Passion Fruit', 'Kiwi', 'Blackberry', 'Pineapple', 'Cranberry', 'Mixed Berry'].map((flavor, index) => {
          // Randomly assign status for demo
          const randomStatus = () => {
            const rand = Math.random();
            if (rand > 0.8) return {
              level: Math.floor(Math.random() * 20) + 1,
              status: 'Critical'
            };
            if (rand > 0.6) return {
              level: Math.floor(Math.random() * 30) + 20,
              status: 'Low'
            };
            return {
              level: Math.floor(Math.random() * 50) + 50,
              status: 'Good'
            };
          };
          const status = randomStatus();
          return <div key={index} className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-3" data-id="element-656">
                <div className="flex items-center justify-between mb-2" data-id="element-657">
                  <div className="flex items-center" data-id="element-658">
                    <div className={`w-3 h-3 rounded-full mr-2 ${status.status === 'Good' ? 'bg-[#A6FF4D]' : status.status === 'Low' ? 'bg-[#FF9800]' : 'bg-[#F44336]'}`} data-id="element-659"></div>
                    <h4 className="text-white text-sm font-medium" data-id="element-660">{flavor}</h4>
                  </div>
                  <span className="text-xs text-gray-400" data-id="element-661">{status.level}%</span>
                </div>
                <div className="h-1 bg-[#1A237E]/60 rounded-full overflow-hidden" data-id="element-662">
                  <div className={`h-full ${status.status === 'Good' ? 'bg-[#A6FF4D]' : status.status === 'Low' ? 'bg-[#FF9800]' : 'bg-[#F44336]'}`} style={{
                width: `${status.level}%`
              }} data-id="element-663"></div>
                </div>
              </div>;
        })}
        </div>
      </GlassCard>
      {/* Recent Refill History */}
      <GlassCard className="mb-6 p-4" data-id="element-664">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-665">
          Recent Refill History
        </h3>
        <div className="space-y-3" data-id="element-666">
          {[{
          date: 'Jun 14, 2023',
          time: '10:25 AM',
          flavor: 'Mango',
          staff: 'Emma W.',
          location: 'Eco Café'
        }, {
          date: 'Jun 12, 2023',
          time: '2:15 PM',
          flavor: 'Berry Blast',
          staff: 'John D.',
          location: 'Eco Café'
        }, {
          date: 'Jun 10, 2023',
          time: '11:30 AM',
          flavor: 'Citrus Fizz',
          staff: 'Emma W.',
          location: 'Eco Café'
        }, {
          date: 'Jun 9, 2023',
          time: '3:45 PM',
          flavor: 'Peach Tea',
          staff: 'John D.',
          location: 'Eco Café'
        }, {
          date: 'Jun 7, 2023',
          time: '9:15 AM',
          flavor: 'CO2 Container',
          staff: 'Mike R.',
          location: 'Eco Café'
        }].map((refill, index) => <div key={index} className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg p-3" data-id="element-667">
              <div className="flex items-center" data-id="element-668">
                <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-669">
                  <CheckIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-670" />
                </div>
                <div data-id="element-671">
                  <p className="text-white" data-id="element-672">
                    <span className="font-medium" data-id="element-673">{refill.flavor}</span>{' '}
                    refilled by {refill.staff}
                  </p>
                  <div className="flex items-center text-sm text-gray-300 mt-1" data-id="element-674">
                    <CalendarIcon className="w-3 h-3 mr-1" data-id="element-675" /> {refill.date},{' '}
                    {refill.time}
                    <span className="mx-2" data-id="element-676">•</span>
                    <MapPinIcon className="w-3 h-3 mr-1" data-id="element-677" /> {refill.location}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </GlassCard>
    </>;
  return <div className="h-full bg-[#1A237E] pt-12 pb-24 px-4 overflow-y-auto" data-id="element-678">
      <header className="flex justify-between items-center mb-6" data-id="element-679">
        <div className="flex items-center" data-id="element-680">
          <div data-id="element-681">
            <h1 className="text-2xl font-bold text-white font-gt-ultra" data-id="element-682">
              Barista Dashboard
            </h1>
            <p className="text-[#00E4BB]" data-id="element-683">Eco Café - Downtown</p>
          </div>
          <Button variant="outline" size="sm" className="border-[#00E4BB]/30 text-[#00E4BB] ml-4" onClick={onLogout} data-id="element-684">
            <LogOutIcon className="w-4 h-4 mr-1" data-id="element-685" /> Logout
          </Button>
        </div>
      </header>
      {/* Filter Controls */}
      <div className="flex space-x-3 mb-6" data-id="element-686">
        <select value={timeRange} onChange={e => setTimeRange(e.target.value)} className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-687">
          <option value="day" data-id="element-688">Today</option>
          <option value="week" data-id="element-689">This Week</option>
          <option value="month" data-id="element-690">This Month</option>
          <option value="year" data-id="element-691">This Year</option>
        </select>
        <select value={location} onChange={e => setLocation(e.target.value)} className="bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-692">
          <option value="all" data-id="element-693">All Locations</option>
          <option value="cafe" data-id="element-694">Eco Café</option>
          <option value="fitness" data-id="element-695">Fitness Center</option>
          <option value="lounge" data-id="element-696">Hydration Lounge</option>
        </select>
      </div>
      {/* Tab Navigation */}
      <div className="flex border-b border-[#00E4BB]/20 mb-6 overflow-x-auto scrollbar-hide" data-id="element-697">
        <button className={`px-3 py-2 text-xs font-medium whitespace-nowrap ${activeTab === 'dashboard' ? 'text-[#00E4BB] border-b-2 border-[#00E4BB]' : 'text-gray-400 hover:text-white'}`} onClick={() => setActiveTab('dashboard')} data-id="element-698">
          Dashboard
        </button>
        <button className={`px-3 py-2 text-xs font-medium whitespace-nowrap ${activeTab === 'campaigns' ? 'text-[#00E4BB] border-b-2 border-[#00E4BB]' : 'text-gray-400 hover:text-white'}`} onClick={() => setActiveTab('campaigns')} data-id="element-699">
          Campaigns
        </button>
        <button className={`px-3 py-2 text-xs font-medium whitespace-nowrap ${activeTab === 'announcements' ? 'text-[#00E4BB] border-b-2 border-[#00E4BB]' : 'text-gray-400 hover:text-white'}`} onClick={() => setActiveTab('announcements')} data-id="element-700">
          Announcements
        </button>
        <button className={`px-3 py-2 text-xs font-medium whitespace-nowrap ${activeTab === 'members' ? 'text-[#00E4BB] border-b-2 border-[#00E4BB]' : 'text-gray-400 hover:text-white'}`} onClick={() => setActiveTab('members')} data-id="element-701">
          Members
        </button>
        <button className={`px-3 py-2 text-xs font-medium whitespace-nowrap ${activeTab === 'refills' ? 'text-[#00E4BB] border-b-2 border-[#00E4BB]' : 'text-gray-400 hover:text-white'}`} onClick={() => setActiveTab('refills')} data-id="element-702">
          Refills
        </button>
      </div>
      {/* Tab Content */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'campaigns' && renderCampaigns()}
      {activeTab === 'announcements' && renderAnnouncements()}
      {activeTab === 'members' && renderMemberActivity()}
      {activeTab === 'refills' && renderRefillStatus()}
      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#1A237E]/90 backdrop-blur-lg border-t border-[#00E4BB]/20 py-1 flex justify-between z-50 px-1" data-id="element-703">
        <button className={`flex flex-col items-center justify-center flex-1 ${activeTab === 'dashboard' ? 'text-[#00E4BB]' : 'text-gray-400'}`} onClick={() => setActiveTab('dashboard')} data-id="element-704">
          <BarChart3Icon className="w-4 h-4" data-id="element-705" />
          <span className="text-[8px]" data-id="element-706">Dashboard</span>
        </button>
        <button className={`flex flex-col items-center justify-center flex-1 ${activeTab === 'campaigns' ? 'text-[#00E4BB]' : 'text-gray-400'}`} onClick={() => setActiveTab('campaigns')} data-id="element-707">
          <TrendingUpIcon className="w-4 h-4" data-id="element-708" />
          <span className="text-[8px]" data-id="element-709">Campaigns</span>
        </button>
        <button className={`flex flex-col items-center justify-center flex-1 ${activeTab === 'announcements' ? 'text-[#00E4BB]' : 'text-gray-400'}`} onClick={() => setActiveTab('announcements')} data-id="element-710">
          <BellIcon className="w-4 h-4" data-id="element-711" />
          <span className="text-[8px]" data-id="element-712">Alerts</span>
        </button>
        <button className={`flex flex-col items-center justify-center flex-1 ${activeTab === 'members' ? 'text-[#00E4BB]' : 'text-gray-400'}`} onClick={() => setActiveTab('members')} data-id="element-713">
          <UsersIcon className="w-4 h-4" data-id="element-714" />
          <span className="text-[8px]" data-id="element-715">Members</span>
        </button>
        <button className={`flex flex-col items-center justify-center flex-1 ${activeTab === 'refills' ? 'text-[#00E4BB]' : 'text-gray-400'}`} onClick={() => setActiveTab('refills')} data-id="element-716">
          <DropletIcon className="w-4 h-4" data-id="element-717" />
          <span className="text-[8px]" data-id="element-718">Refills</span>
        </button>
      </div>
    </div>;
};
export default BaristaAdmin;